/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type { BasicFailedReply, BasicFailedReplyElement } from '@modules/cloud-api/v1/types'
import type { AsyncRequestState, SliderInstanceType } from '@modules/ui-types'
import { getStatus } from '@modules/utils/error'

import { getSupportedSliderInstanceTypes } from '@/lib/sliders/support'

export function getValidationWarnings({
  updateDeploymentDryRunRequest,
}: {
  updateDeploymentDryRunRequest: AsyncRequestState
}): BasicFailedReplyElement[] {
  if (updateDeploymentDryRunRequest.error) {
    const badRequest = getStatus(updateDeploymentDryRunRequest.error) === 400
    const body = (updateDeploymentDryRunRequest.error as any).body as BasicFailedReply

    if (badRequest && body) {
      const { errors } = body

      if (!errors) {
        return []
      }

      return errors
    }

    return []
  }

  return []
}

export function decorateWarningsWithSliderType(
  warnings: BasicFailedReplyElement[],
): Array<BasicFailedReplyElement & { sliderInstanceType: SliderInstanceType | undefined }> {
  return warnings.map((warning) => ({
    ...warning,
    sliderInstanceType: deriveSliderInstanceTypeFromWarning(warning),
  }))
}

function deriveSliderInstanceTypeFromWarning(
  warning: BasicFailedReplyElement,
): SliderInstanceType | undefined {
  return getSupportedSliderInstanceTypes().find((sliderInstanceType) => {
    if (!warning.fields) {
      return false
    }

    return warning.fields.every(
      // an example of fields for slider resources we're looking for are:
      // "resources.kibana[0].kibana.user_settings_json"
      (field) => field.startsWith(`resources.${sliderInstanceType}`),
    )
  })
}
